import React, { useState } from 'react';
import AdminSidebar from '../../components/ui/AdminSidebar';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const UserBehaviorAnalyticsCenter = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [dateRange, setDateRange] = useState('30days');
  const [selectedSegment, setSelectedSegment] = useState('all');
  const [activeTab, setActiveTab] = useState('journey');

  const journeyMapData = [
    { step: 'Landing', users: 12450, dropoff: 8, avgTime: '45s', conversion: 92 },
    { step: 'Signup', users: 11454, dropoff: 12, avgTime: '2m 15s', conversion: 88 },
    { step: 'Onboarding', users: 10079, dropoff: 15, avgTime: '5m 30s', conversion: 85 },
    { step: 'First Feature', users: 8567, dropoff: 10, avgTime: '8m 20s', conversion: 90 },
    { step: 'Active User', users: 7710, dropoff: 5, avgTime: '12m 45s', conversion: 95 }
  ];

  const engagementMetrics = [
    { metric: 'Avg Session Duration', value: '12m 34s', change: 15.3, trend: 'up', target: '> 10m' },
    { metric: 'Page Views per Session', value: '8.4', change: 12.1, trend: 'up', target: '> 7' },
    { metric: 'Click-Through Rate', value: '24.6%', change: 8.7, trend: 'up', target: '> 20%' },
    { metric: 'Bounce Rate', value: '18.2%', change: -5.4, trend: 'down', target: '< 25%' },
    { metric: 'Return Visitor Rate', value: '68.5%', change: 22.3, trend: 'up', target: '> 60%' },
    { metric: 'Feature Adoption', value: '84.3%', change: 18.9, trend: 'up', target: '> 75%' }
  ];

  const cohortData = [
    { cohort: 'Jan 2026', size: 1247, week1: 89, week2: 78, week3: 72, week4: 68, retention: 68, churn: 32 },
    { cohort: 'Dec 2025', size: 1089, week1: 92, week2: 81, week3: 75, week4: 71, retention: 71, churn: 29 },
    { cohort: 'Nov 2025', size: 956, week1: 88, week2: 76, week3: 69, week4: 64, retention: 64, churn: 36 },
    { cohort: 'Oct 2025', size: 834, week1: 91, week2: 79, week3: 73, week4: 69, retention: 69, churn: 31 },
    { cohort: 'Sep 2025', size: 723, week1: 87, week2: 74, week3: 67, week4: 62, retention: 62, churn: 38 }
  ];

  const featureUsageData = [
    { feature: 'Predictive Analysis', adoption: 78, timeToValue: '3.2 days', abandonment: 12, usage: 4521 },
    { feature: 'Business Canvas', adoption: 85, timeToValue: '1.8 days', abandonment: 8, usage: 3845 },
    { feature: 'Goal Intelligence', adoption: 72, timeToValue: '4.5 days', abandonment: 15, usage: 2967 },
    { feature: 'Reports Dashboard', adoption: 91, timeToValue: '1.2 days', abandonment: 5, usage: 5234 },
    { feature: 'Billing Management', adoption: 95, timeToValue: '0.8 days', abandonment: 3, usage: 6123 }
  ];

  const userSegments = [
    { segment: 'Power Users', count: 1247, engagement: 95, revenue: '$124,700', growth: 23.4 },
    { segment: 'Regular Users', count: 3456, engagement: 78, revenue: '$172,800', growth: 15.7 },
    { segment: 'Occasional Users', count: 2189, engagement: 45, revenue: '$65,670', growth: 8.2 },
    { segment: 'At-Risk Users', count: 892, engagement: 28, revenue: '$26,760', growth: -12.3 },
    { segment: 'Churned Users', count: 567, engagement: 5, revenue: '$5,670', growth: -45.6 }
  ];

  const realTimeActivity = [
    { metric: 'Active Users Now', value: 847, icon: 'Users', color: 'emerald' },
    { metric: 'Concurrent Sessions', value: 1234, icon: 'Activity', color: 'blue' },
    { metric: 'Live Feature Usage', value: 456, icon: 'Zap', color: 'violet' },
    { metric: 'Geographic Locations', value: 89, icon: 'Globe', color: 'amber' }
  ];

  const getTrendColor = (trend) => {
    return trend === 'up' ? 'text-emerald-600' : trend === 'down' ? 'text-red-600' : 'text-slate-600';
  };

  const getRetentionColor = (value) => {
    if (value >= 70) return 'bg-emerald-500';
    if (value >= 50) return 'bg-amber-500';
    return 'bg-red-500';
  };

  return (
    <div className="min-h-screen bg-slate-50 flex">
      <AdminSidebar isCollapsed={sidebarCollapsed} />
      <div className={`flex-1 transition-all duration-300 ${sidebarCollapsed ? 'ml-20' : 'ml-64'}`}>
        {/* Header */}
        <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
          <div className="px-8 py-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h1 className="text-3xl font-bold text-slate-900">User Behavior Analytics Center</h1>
                <p className="text-slate-600 mt-1">Comprehensive user interaction analysis and behavioral tracking</p>
              </div>
              <div className="flex items-center gap-3">
                <select
                  value={dateRange}
                  onChange={(e) => setDateRange(e?.target?.value)}
                  className="px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                >
                  <option value="7days">Last 7 Days</option>
                  <option value="30days">Last 30 Days</option>
                  <option value="90days">Last 90 Days</option>
                  <option value="year">Last Year</option>
                </select>
                <select
                  value={selectedSegment}
                  onChange={(e) => setSelectedSegment(e?.target?.value)}
                  className="px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                >
                  <option value="all">All Users</option>
                  <option value="power">Power Users</option>
                  <option value="regular">Regular Users</option>
                  <option value="atrisk">At-Risk Users</option>
                </select>
                <Button className="bg-violet-600 hover:bg-violet-700 text-white px-6 py-2.5 rounded-lg font-medium flex items-center gap-2">
                  <Icon name="Download" className="w-5 h-5" />
                  Export Report
                </Button>
              </div>
            </div>

            {/* Tab Navigation */}
            <div className="flex items-center gap-2 border-b border-slate-200">
              {[
                { id: 'journey', label: 'User Journey', icon: 'Map' },
                { id: 'engagement', label: 'Engagement', icon: 'Activity' },
                { id: 'cohort', label: 'Cohort Analysis', icon: 'Users' },
                { id: 'features', label: 'Feature Usage', icon: 'Zap' },
                { id: 'segments', label: 'Segmentation', icon: 'Filter' },
                { id: 'realtime', label: 'Real-Time', icon: 'Radio' }
              ]?.map((tab) => (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`px-4 py-3 font-medium text-sm flex items-center gap-2 border-b-2 transition-colors ${
                    activeTab === tab?.id
                      ? 'border-violet-600 text-violet-600'
                      : 'border-transparent text-slate-600 hover:text-slate-900'
                  }`}
                >
                  <Icon name={tab?.icon} className="w-4 h-4" />
                  {tab?.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="p-8">
          {/* User Journey Map */}
          {activeTab === 'journey' && (
            <div className="space-y-6">
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <h2 className="text-xl font-bold text-slate-900 mb-6">User Journey Flow</h2>
                <div className="space-y-4">
                  {journeyMapData?.map((step, index) => (
                    <div key={index} className="relative">
                      <div className="flex items-center gap-4">
                        <div className="flex-shrink-0 w-32">
                          <div className="text-sm font-medium text-slate-900">{step?.step}</div>
                          <div className="text-xs text-slate-500">{step?.avgTime}</div>
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-3">
                            <div className="flex-1 bg-slate-200 rounded-full h-8 relative overflow-hidden">
                              <div
                                className="bg-gradient-to-r from-violet-500 to-blue-500 h-full rounded-full flex items-center justify-between px-4"
                                style={{ width: `${step?.conversion}%` }}
                              >
                                <span className="text-white text-sm font-medium">{step?.users?.toLocaleString()} users</span>
                                <span className="text-white text-sm font-medium">{step?.conversion}%</span>
                              </div>
                            </div>
                            <div className="flex-shrink-0 w-24 text-right">
                              <div className="text-sm font-medium text-red-600">{step?.dropoff}% drop</div>
                            </div>
                          </div>
                        </div>
                      </div>
                      {index < journeyMapData?.length - 1 && (
                        <div className="ml-16 mt-2 mb-2">
                          <Icon name="ArrowDown" className="w-5 h-5 text-slate-400" />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Engagement Analytics */}
          {activeTab === 'engagement' && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {engagementMetrics?.map((metric, index) => (
                <div key={index} className="bg-white rounded-xl border border-slate-200 p-6">
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-slate-600 text-sm font-medium">{metric?.metric}</span>
                    <div className={`flex items-center gap-1 ${getTrendColor(metric?.trend)}`}>
                      <Icon name={metric?.trend === 'up' ? 'TrendingUp' : 'TrendingDown'} className="w-4 h-4" />
                      <span className="text-sm font-medium">{Math.abs(metric?.change)}%</span>
                    </div>
                  </div>
                  <div className="text-3xl font-bold text-slate-900 mb-2">{metric?.value}</div>
                  <div className="text-sm text-slate-500">Target: {metric?.target}</div>
                  <div className="mt-4 h-2 bg-slate-200 rounded-full overflow-hidden">
                    <div className="h-full bg-violet-600 rounded-full" style={{ width: `${metric?.change + 50}%` }}></div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Cohort Analysis */}
          {activeTab === 'cohort' && (
            <div className="bg-white rounded-xl border border-slate-200">
              <div className="px-6 py-4 border-b border-slate-200">
                <h2 className="text-xl font-bold text-slate-900">Cohort Retention Analysis</h2>
                <p className="text-slate-600 text-sm mt-1">Weekly retention patterns by user cohort</p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase">Cohort</th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase">Size</th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase">Week 1</th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase">Week 2</th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase">Week 3</th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase">Week 4</th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase">Retention</th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase">Churn</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    {cohortData?.map((cohort, index) => (
                      <tr key={index} className="hover:bg-slate-50">
                        <td className="px-6 py-4 font-medium text-slate-900">{cohort?.cohort}</td>
                        <td className="px-6 py-4 text-slate-700">{cohort?.size?.toLocaleString()}</td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <div className={`w-12 h-6 rounded ${getRetentionColor(cohort?.week1)}`}></div>
                            <span className="text-sm font-medium text-slate-700">{cohort?.week1}%</span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <div className={`w-12 h-6 rounded ${getRetentionColor(cohort?.week2)}`}></div>
                            <span className="text-sm font-medium text-slate-700">{cohort?.week2}%</span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <div className={`w-12 h-6 rounded ${getRetentionColor(cohort?.week3)}`}></div>
                            <span className="text-sm font-medium text-slate-700">{cohort?.week3}%</span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <div className={`w-12 h-6 rounded ${getRetentionColor(cohort?.week4)}`}></div>
                            <span className="text-sm font-medium text-slate-700">{cohort?.week4}%</span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className="px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-sm font-medium">
                            {cohort?.retention}%
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <span className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm font-medium">
                            {cohort?.churn}%
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Feature Usage */}
          {activeTab === 'features' && (
            <div className="bg-white rounded-xl border border-slate-200">
              <div className="px-6 py-4 border-b border-slate-200">
                <h2 className="text-xl font-bold text-slate-900">Feature Usage Analytics</h2>
                <p className="text-slate-600 text-sm mt-1">Adoption rates and time-to-value metrics</p>
              </div>
              <div className="p-6 space-y-6">
                {featureUsageData?.map((feature, index) => (
                  <div key={index} className="border border-slate-200 rounded-lg p-5">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-bold text-slate-900">{feature?.feature}</h3>
                      <span className="px-3 py-1 bg-violet-100 text-violet-700 rounded-full text-sm font-medium">
                        {feature?.adoption}% Adoption
                      </span>
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <div className="text-sm text-slate-600 mb-1">Time to Value</div>
                        <div className="text-xl font-bold text-slate-900">{feature?.timeToValue}</div>
                      </div>
                      <div>
                        <div className="text-sm text-slate-600 mb-1">Abandonment Rate</div>
                        <div className="text-xl font-bold text-slate-900">{feature?.abandonment}%</div>
                      </div>
                      <div>
                        <div className="text-sm text-slate-600 mb-1">Total Usage</div>
                        <div className="text-xl font-bold text-slate-900">{feature?.usage?.toLocaleString()}</div>
                      </div>
                    </div>
                    <div className="mt-4">
                      <div className="flex items-center justify-between text-sm text-slate-600 mb-2">
                        <span>Adoption Progress</span>
                        <span>{feature?.adoption}%</span>
                      </div>
                      <div className="h-3 bg-slate-200 rounded-full overflow-hidden">
                        <div className="h-full bg-gradient-to-r from-violet-500 to-blue-500 rounded-full" style={{ width: `${feature?.adoption}%` }}></div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* User Segmentation */}
          {activeTab === 'segments' && (
            <div className="grid grid-cols-1 gap-6">
              {userSegments?.map((segment, index) => (
                <div key={index} className="bg-white rounded-xl border border-slate-200 p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="text-lg font-bold text-slate-900">{segment?.segment}</h3>
                      <p className="text-slate-600 text-sm mt-1">{segment?.count?.toLocaleString()} users in this segment</p>
                    </div>
                    <div className={`flex items-center gap-1 ${segment?.growth >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                      <Icon name={segment?.growth >= 0 ? 'TrendingUp' : 'TrendingDown'} className="w-5 h-5" />
                      <span className="text-lg font-bold">{Math.abs(segment?.growth)}%</span>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-6">
                    <div>
                      <div className="text-sm text-slate-600 mb-2">Engagement Score</div>
                      <div className="flex items-center gap-3">
                        <div className="flex-1 h-3 bg-slate-200 rounded-full overflow-hidden">
                          <div className="h-full bg-violet-600 rounded-full" style={{ width: `${segment?.engagement}%` }}></div>
                        </div>
                        <span className="text-lg font-bold text-slate-900">{segment?.engagement}%</span>
                      </div>
                    </div>
                    <div>
                      <div className="text-sm text-slate-600 mb-2">Total Revenue</div>
                      <div className="text-2xl font-bold text-slate-900">{segment?.revenue}</div>
                    </div>
                    <div>
                      <div className="text-sm text-slate-600 mb-2">Growth Rate</div>
                      <div className={`text-2xl font-bold ${segment?.growth >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                        {segment?.growth >= 0 ? '+' : ''}{segment?.growth}%
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Real-Time Activity */}
          {activeTab === 'realtime' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                {realTimeActivity?.map((item, index) => (
                  <div key={index} className="bg-white rounded-xl border border-slate-200 p-6">
                    <div className="flex items-center justify-between mb-4">
                      <Icon name={item?.icon} className={`w-8 h-8 text-${item?.color}-600`} />
                      <div className="w-3 h-3 bg-emerald-500 rounded-full animate-pulse"></div>
                    </div>
                    <div className="text-3xl font-bold text-slate-900 mb-2">{item?.value?.toLocaleString()}</div>
                    <div className="text-sm text-slate-600">{item?.metric}</div>
                  </div>
                ))}
              </div>

              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <h2 className="text-xl font-bold text-slate-900 mb-6">Live Activity Feed</h2>
                <div className="space-y-3">
                  {[
                    { user: 'John Doe', action: 'completed Predictive Analysis', time: '2 seconds ago', type: 'success' },
                    { user: 'Jane Smith', action: 'started Business Canvas', time: '15 seconds ago', type: 'info' },
                    { user: 'Mike Johnson', action: 'upgraded to Pro plan', time: '1 minute ago', type: 'success' },
                    { user: 'Sarah Williams', action: 'exported report', time: '2 minutes ago', type: 'info' },
                    { user: 'Tom Brown', action: 'set new goal', time: '3 minutes ago', type: 'info' }
                  ]?.map((activity, index) => (
                    <div key={index} className="flex items-center gap-4 p-4 bg-slate-50 rounded-lg">
                      <div className={`w-2 h-2 rounded-full ${activity?.type === 'success' ? 'bg-emerald-500' : 'bg-blue-500'}`}></div>
                      <div className="flex-1">
                        <span className="font-medium text-slate-900">{activity?.user}</span>
                        <span className="text-slate-600"> {activity?.action}</span>
                      </div>
                      <span className="text-sm text-slate-500">{activity?.time}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserBehaviorAnalyticsCenter;